import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { getPublicPilotProfile } from "@/lib/pilot-self.functions";
import { Award, Sparkles, Instagram, Music2, Youtube } from "lucide-react";
import logoAsset from "@/assets/logo.png.asset.json";

export const Route = createFileRoute("/pilots/$callsign")({
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(publicPilotQueryOptions(params.callsign)),
  head: ({ params }) => ({
    meta: [
      { title: `${params.callsign} — SquadFlight Pilot` },
      {
        name: "description",
        content: `Pilot card for ${params.callsign} at SquadFlight FPV, the indoor micro-FPV drone gym.`,
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: `${params.callsign} — SquadFlight Pilot` },
      {
        property: "og:description",
        content: `Pilot card for ${params.callsign} at SquadFlight FPV.`,
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PublicPilotPage,
  errorComponent: () => (
    <ProfileShell>
      <p className="text-sm text-slate-400">This pilot card couldn't be loaded.</p>
    </ProfileShell>
  ),
  notFoundComponent: () => (
    <ProfileShell>
      <p className="text-sm text-slate-400">No public pilot card here.</p>
    </ProfileShell>
  ),
});

function publicPilotQueryOptions(callsign: string) {
  return queryOptions({
    queryKey: ["public-pilot", callsign],
    queryFn: () => getPublicPilotProfile({ data: { callsign } }),
  });
}

function ProfileShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-950 px-5 py-12 text-slate-100">
      <div className="mx-auto max-w-md">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-8 text-center shadow-2xl backdrop-blur">
          {children}
        </div>
      </div>
    </div>
  );
}

function PublicPilotPage() {
  const { callsign } = Route.useParams();
  const { data: profile } = useSuspenseQuery(publicPilotQueryOptions(callsign));

  if (!profile) throw notFound();

  return (
    <ProfileShell>
      <img
        src={logoAsset.url}
        alt="SquadFlight FPV"
        width={72}
        height={72}
        className="mx-auto size-18 rounded-2xl"
      />
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">{profile.callsign}</h1>
      {profile.member_since && (
        <p className="mt-2 text-sm text-slate-400">
          Flying with SquadFlight since{" "}
          {new Date(profile.member_since).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          })}
        </p>
      )}

      {profile.bio && <p className="mt-5 text-sm leading-relaxed text-slate-300">{profile.bio}</p>}

      <div className="mt-7 grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
          <Sparkles className="mx-auto size-5 text-cyan-400" />
          <p className="mt-2 font-display text-2xl font-bold">{profile.xp_points}</p>
          <p className="text-xs uppercase tracking-wide text-slate-500">Flight XP</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
          <Award className="mx-auto size-5 text-fuchsia-400" />
          <p className="mt-2 font-display text-2xl font-bold">{profile.community_clout}</p>
          <p className="text-xs uppercase tracking-wide text-slate-500">Clout</p>
        </div>
      </div>

      {(profile.instagram || profile.tiktok || profile.youtube_url) && (
        <div className="mt-7 flex items-center justify-center gap-4">
          {profile.instagram && (
            <SocialLink
              href={`https://instagram.com/${profile.instagram.replace(/^@/, "")}`}
              label="Instagram"
            >
              <Instagram className="size-5" />
            </SocialLink>
          )}
          {profile.tiktok && (
            <SocialLink
              href={`https://tiktok.com/@${profile.tiktok.replace(/^@/, "")}`}
              label="TikTok"
            >
              <Music2 className="size-5" />
            </SocialLink>
          )}
          {profile.youtube_url && (
            <SocialLink href={profile.youtube_url} label="YouTube">
              <Youtube className="size-5" />
            </SocialLink>
          )}
        </div>
      )}
    </ProfileShell>
  );
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="rounded-xl border border-slate-800 p-3 text-slate-400 transition-colors hover:border-cyan-500 hover:text-cyan-400"
    >
      {children}
    </a>
  );
}
